/**
 * AsteroidSystem - Handles moving asteroid hazards
 * 
 * Creates moving asteroids (Space level).
 * Asteroids are deadly hazards.
 * 
 * @module systems/AsteroidSystem
 */

import Phaser from 'phaser';
import BaseSystem from './BaseSystem.js';

export default class AsteroidSystem extends BaseSystem {
    constructor(scene) {
        super(scene);
        this.label = 'asteroid';
    }

    /**
     * Create asteroids from level config
     * @param {Object} config - Level configuration
     */
    create(config) {
        if (!config.asteroids) return;

        config.asteroids.forEach(a => {
            const body = this.scene.matter.add.circle(a.x, a.y, a.radius, {
                isStatic: false,
                isSensor: true,
                label: this.label,
                frictionAir: 0
            });

            // Random initial velocity
            this.scene.matter.body.setVelocity(body, {
                x: Math.cos(Math.random() * Math.PI * 2) * 2,
                y: Math.sin(Math.random() * Math.PI * 2) * 2
            });

            // Visual
            const visual = this.scene.add.graphics();
            visual.fillStyle(0x666666, 1);
            visual.fillCircle(0, 0, a.radius);
            visual.fillStyle(0x888888, 0.5);
            visual.fillCircle(-a.radius * 0.3, -a.radius * 0.3, a.radius * 0.3);
            visual.x = a.x;
            visual.y = a.y;

            this.items.push({ 
                body, 
                visual, 
                config: a,
                offsetX: 0,
                offsetY: 0
            });
        });

        this.isActive = this.items.length > 0;
    }

    /**
     * Check if a body belongs to this system
     * @param {Object} body - Physics body
     * @returns {boolean}
     */
    ownsBody(body) {
        return body.label === this.label;
    }

    /**
     * Handle collision - asteroids kill player
     * @param {Object} body - Asteroid body
     * @param {Object} player - Player reference
     * @returns {boolean} True if collision handled
     */
    handleCollision(body, player) {
        if (!this.ownsBody(body)) return false;
        return true; // Signal player death
    }

    /**
     * Get tracked bodies for visual sync
     * @returns {Array}
     */
    getTrackedBodies() {
        return this.items.map(item => ({
            body: item.body,
            visuals: [item.visual],
            offsetX: item.offsetX,
            offsetY: item.offsetY
        }));
    }

    /**
     * Update visual positions to match physics
     * @param {number} delta - Delta time
     */
    update(delta) {
        if (!this.isActive) return;

        this.items.forEach(item => {
            if (item.body && item.body.position && item.visual) {
                item.visual.x = item.body.position.x;
                item.visual.y = item.body.position.y;
            }
        });
    }
}
